import reportitemledgerService from '../../../services/reportitemledgerService';
import filterService from '../../../services/filterService';

import {
    setReportitemledgerLoading,
    setReportitemledgerError,
    setReportitemledgerList,
    setReportitemledgerCurrent,
    setReportitemledgerPagination,
    setReportitemledgerSort,
    setReportitemledgerFilters,
    setReportitemledgerDefaultFilter,
    setReportitemledgerSettings,
    clearReportitemledgerCurrent,
    clearReportitemledgerError,
    setReportitemledgerFilterLoading,
    changeReportitemledgerFilterCondition,
    addReportitemledgerFilterCondition,
    updateReportitemledgerFilterConditionRule,
    updateReportitemledgerFilterConditionValue,
    removeReportitemledgerFilterCondition,
    setReportitemledgerSelected,
    setReportitemledgerDownloading,
    resetReportitemledgerFiltersAndPagination,

    setReportitemledgerClassicFilters
} from './reportitemledgerSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportitemledger.isSettingsInitialized) {
            dispatch(setReportitemledgerLoading(true));
            const response = await reportitemledgerService.getSettings();

            // Set the settings
            dispatch(setReportitemledgerSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportitemledgerClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportitemledgerDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportitemledgerError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportitemledgerLoading(false));
    }
};

export const fetchReportitemledgers = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportitemledger;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportitemledgerLoading(true));
        const response = await reportitemledgerService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportitemledgerList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReportitemledgers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportitemledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemledgerLoading(false));
    }
};

export const fetchReportitemledger = (id) => async (dispatch) => {
    try {
        dispatch(setReportitemledgerLoading(true));
        const response = await reportitemledgerService.getOne(id);
        dispatch(setReportitemledgerCurrent(response.data));
    } catch (error) {
        dispatch(setReportitemledgerError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportitemledgerLoading(false));
    }
};

export const createReportitemledger = (data) => async (dispatch) => {
    try {
        const response = await reportitemledgerService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportitemledger = (id, data) => async (dispatch) => {
    try {
        const response = await reportitemledgerService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportitemledger = (id) => async (dispatch) => {
    try {
        dispatch(setReportitemledgerLoading(true));
        const response = await reportitemledgerService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportitemledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemledgerLoading(false));
    }
};

export const downloadReportitemledger = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportitemledger;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportitemledgerDownloading(true));
        const response = await reportitemledgerService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportitemledgerDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportitemledgers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportitemledgerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportitemledgerDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportitemledgerPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportitemledgerSort(sort));
};

export const searchReportitemledgers = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportitemledger.filters;
    dispatch(setReportitemledgerFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportitemledgerDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportitemledgerFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportitemledgerFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportitemledgerFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportitemledgerFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportitemledgerFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportitemledgerFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportitemledger.settings;
            dispatch(setReportitemledgerSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportitemledgerFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportitemledgerFilterLoading(false));
        dispatch(setReportitemledgerError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportitemledgerSelected(selectedRows));
    };
};

export const clearCurrentReportitemledger = () => {
    return (dispatch) => {
        dispatch(clearReportitemledgerCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportitemledgerFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportitemledger.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportitemledgerClassicFilters(updatedClassicFilters));
};